import { defineStore } from 'pinia'

interface BillingState {
  featureFlags: { [key: string]: boolean } | null
  currentPlan: string | null
  effectivePlan: string | null
  currentInterval: string | null
  isSubscribed: boolean
  trialActive: boolean
  subscriptionStatus: string | null
  isLoading: boolean
}

export const useBillingStore = defineStore('billing', {
  state: (): BillingState => ({
    featureFlags: null,
    currentPlan: null,
    effectivePlan: null,
    currentInterval: null,
    isSubscribed: false,
    trialActive: false,
    subscriptionStatus: null,
    isLoading: false
  }),

  actions: {
    async fetchFeatureFlags(force = false) {
      const authStore: any = useAuthStore()
      
      // Only fetch for customer-admin and user roles
      if(authStore.user.role !== 'customer-admin' && authStore.user.role !== 'user') {
        return;
      }

      // If already loaded and not forced, return
      if(this.featureFlags && !force) {
        return;
      }

      this.isLoading = true;

      try {
        const summaryResponse = await fetchBillingSummary()
        
        if(summaryResponse?.data) {
          this.featureFlags = summaryResponse.data.features_flags || null
          this.currentPlan = summaryResponse.data.current_plan || null
          this.effectivePlan = summaryResponse.data.effective_plan || null
          this.currentInterval = summaryResponse.data.current_interval || null
          this.isSubscribed = summaryResponse.data.is_subscribed || false
          this.trialActive = summaryResponse.data.trial_active || false
          this.subscriptionStatus = summaryResponse.data.subscription_status || null
        }
      } catch (error) {
        console.error('Failed to fetch billing summary:', error)
      } finally {
        this.isLoading = false;
      }
    },

    // Force refresh feature flags (useful after subscription changes)
    async refreshFeatureFlags() {
      return await this.fetchFeatureFlags(true);
    },

    // Check if user has access to a specific feature
    hasFeature(featurePath: string): boolean {
      if(!this.featureFlags) {
        return false;
      }
      return this.featureFlags[featurePath] === true;
    },

    // Check if user has valid subscription or trial
    hasValidSubscription(): boolean {
      return this.isSubscribed || this.trialActive;
    },

    // Reset state (on logout)
    resetState() {
      this.featureFlags = null;
      this.currentPlan = null;
      this.effectivePlan = null;
      this.currentInterval = null;
      this.isSubscribed = false;
      this.trialActive = false;
      this.subscriptionStatus = null;
      this.isLoading = false;
    },

    // Get static feature list based on plan
    getFeatureList(planKey: string): string[] {
      // Basic plan features
      if (planKey === 'basic') {
        return [
          'billing.features.web_mobile_apps',
          'billing.features.light_controls',
          'billing.features.match_mode',
          'billing.features.unlimited_users',
          'billing.features.roles_permissions'
        ];
      }
      
      // Plus plan features (additional to Basic)
      if (planKey === 'plus') {
        return [
          'billing.features.smart_time_scheduling',
          'billing.features.event_scheduling',
          'billing.features.energy_monitoring',
          'billing.features.activity_log_feature'
        ];
      }
      
      // Premium plan features (additional to Plus)
      if (planKey === 'premium') {
        return [
          'billing.features.apis_integration',
          'billing.features.club_knowledge_base',
          'billing.features.streetlights_integration'
        ];
      }

      return [];
    }
  }
})

